<template>
  <div class="star-rating flex-row">
    <div
      v-for="(star, idx) in stars"
      :key="idx"
      class="star-rating__star-container"
      :class="[editable && 'star-rating__star-container--editable']"
      @click="handleClick(idx)"
    >
      <!-- Background star (always regular for partial stars, or the actual weight for full/empty) -->
      <PhStar
        :class="['status-warning-alt']"
        :weight="star.partial ? 'regular' : star.weight"
        :size="iconSize"
      />
      <!-- Foreground filled star (only for partial stars) -->
      <PhStar
        v-if="star.partial"
        :class="['star-rating__star--filled status-warning-alt']"
        :style="{ '--fill-percentage': `${star.fillPercentage}%` }"
        weight="fill"
        :size="iconSize"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PhStar } from '@phosphor-icons/vue'
import { computed } from 'vue'

import { useStarRating } from '@/composables/useStarRating'

const props = defineProps({
  /**
   * Star rating can be changed (use model)
   */
  editable: { default: false, type: Boolean },

  /**
   * Star rating model
   * @model
   */
  modelValue: { default: undefined, type: Number },

  /**
   * Rating (1-5), supports decimal values for partial ratings
   */
  rating: { default: undefined, type: Number },

  /**
   * Star icon size
   */
  size: { default: undefined, type: String },
})

const emit = defineEmits(['update:modelValue'])

// Model binding for editable mode
const starModel = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const effectiveRating = computed(() => {
  if (props.editable) return starModel.value
  return props.rating
})

const iconSize = computed(() => {
  if (props.size === 'lg') {
    return 24
  }
  return 16
})

// Use the extracted star rating logic
const { stars } = useStarRating(effectiveRating)

const handleClick = (index: number) => {
  if (!props.editable) return
  starModel.value = index + 1
}
</script>
<style lang="scss" scoped>
.star-rating {
  &__star-container {
    position: relative;
    display: inline-block;

    &--editable {
      cursor: pointer;
    }
  }

  &__star {
    &--filled {
      position: absolute;
      top: 0;
      left: 0;
      clip-path: inset(0 calc(100% - var(--fill-percentage)) 0 0);
    }
  }
}
</style>
